import { useEffect, useMemo, useState } from "react"
import { Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Label,
} from "@f2e/ui/components/base"

import { ErrorCode, getLimitErrorTranslationKey } from "@/constants/errors"
import { Agent, ClientChannel, Workspace, isWidgetClientConfig } from "@/types"
import { ConfigSummary } from "./ConfigSummary"
import {
  FetchStatus,
  agentResponseToAgent,
  clientChannelResponseToClientChannel,
  useGetPublishedAgentsApi,
  usePublishClientChannelApi,
} from "@/hooks/api"
import { useToast } from "@/hooks"
import {
  getUserFacingApiErrorMessage,
  normalizeApiError,
} from "@/lib/apiErrors"

export interface ChannelPublishDialogProps {
  open: boolean
  channel: ClientChannel
  workspace: Workspace
  onOpenChange: (open: boolean) => void
  onChannelPublished?: (channel: ClientChannel) => void
}

const getNextVersion = (currentVersion?: string) => {
  if (!currentVersion) {
    return "1.0.0"
  }

  const parts = currentVersion.split(".")
  if (parts.length !== 3) {
    return currentVersion
  }

  const [major, minor, patch] = parts.map((part) => Number(part))
  if (Number.isNaN(major) || Number.isNaN(minor) || Number.isNaN(patch)) {
    return currentVersion
  }

  const nextPatch = patch + 1
  return `${major}.${minor}.${nextPatch}`
}

export const ChannelPublishDialog = ({
  open,
  channel,
  workspace,
  onOpenChange,
  onChannelPublished,
}: ChannelPublishDialogProps) => {
  const { t } = useTranslation()
  const { alertToast } = useToast()
  const [publishedAgents, setPublishedAgents] = useState<Agent[]>([])
  const [version, setVersion] = useState(
    getNextVersion(channel.lastPublishedVersion)
  )
  const [versionTouched, setVersionTouched] = useState(false)

  const trimmedVersion = version.trim()
  const isVersionValid =
    trimmedVersion.length > 0 && trimmedVersion.length <= 20

  // APIs
  const {
    status: publishStatus,
    error: publishError,
    data: publishRespData,
    fetchFn: publishChannel,
  } = usePublishClientChannelApi(
    workspace.organizationId || "",
    workspace.id,
    channel.id
  )
  const { data: agentsRespData, fetchFn: getPublishedAgents } =
    useGetPublishedAgentsApi()

  useEffect(() => {
    if (open) {
      setVersion(getNextVersion(channel.lastPublishedVersion))
      getPublishedAgents({
        searchParams: new URLSearchParams({
          wid: workspace.id,
          oid: workspace.organizationId || "",
        }),
      })
    }
  }, [
    open,
    channel.lastPublishedVersion,
    getPublishedAgents,
    workspace.id,
    workspace.organizationId,
  ])

  useEffect(() => {
    if (!agentsRespData?.data) return
    setPublishedAgents(agentsRespData.data.map(agentResponseToAgent))
  }, [agentsRespData])

  useEffect(() => {
    if (!publishRespData || !publishRespData.data) return
    const updatedChannel = clientChannelResponseToClientChannel(
      publishRespData.data
    )
    onChannelPublished?.(updatedChannel)
    onOpenChange(false)
  }, [publishRespData, onChannelPublished, onOpenChange])

  useEffect(() => {
    if (!publishError) return
    const normalizedError = normalizeApiError(publishError)
    const publishErrorKey =
      normalizedError.kind === "permission_denied"
        ? null
        : normalizedError.errorCode ===
            ErrorCode.PUBLISHED_CHANNEL_PUBLISH_IN_PROGRESS
          ? "workspace.channels.messages.error.publish.inProgress"
          : normalizedError.status === 503
            ? "workspace.channels.messages.error.publish.unavailable"
            : getLimitErrorTranslationKey(
                normalizedError.errorCode ?? undefined
              )
    alertToast({
      title: t("workspace.channels.messages.error.publish.title"),
      description: publishErrorKey
        ? t(publishErrorKey)
        : getUserFacingApiErrorMessage(
            publishError,
            t,
            publishRespData?.message ||
              t("workspace.channels.messages.error.publish.description")
          ),
    })
  }, [publishError, publishRespData, alertToast, t])

  const handleCancel = () => {
    onOpenChange(false)
    setVersionTouched(false)
  }

  const handleConfirm = () => {
    if (publishStatus === FetchStatus.Loading || !isVersionValid) return
    publishChannel({
      jsonBody: {
        version: trimmedVersion,
      },
    })
  }

  const selectedWidgetAgent = useMemo(() => {
    const widgetConfig = isWidgetClientConfig(channel.config)
      ? channel.config
      : null

    if (!widgetConfig?.agentId) {
      return undefined
    }

    return publishedAgents.find((agent) => agent.id === widgetConfig.agentId)
  }, [publishedAgents, channel.config])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>
            {t("workspace.agent.publish.dialog.confirm.title")}
          </DialogTitle>
          <DialogDescription>
            {t("workspace.agent.publish.dialog.confirm.description", {
              name: channel.name,
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="channel-publish-version">
              {t("workspace.channels.overview.basicInfo.version")}
            </Label>
            <Input
              id="channel-publish-version"
              value={version}
              onChange={(event) => {
                setVersion(event.target.value)
                if (!versionTouched) {
                  setVersionTouched(true)
                }
              }}
              className="font-mono"
            />
            {versionTouched && !isVersionValid && (
              <p className="text-xs text-red-500">
                {t(
                  "workspace.channels.messages.error.publish.versionLength",
                  "Version must be between 1 and 20 characters."
                )}
              </p>
            )}
          </div>

          <div className="space-y-2 text-sm">
            <div className="font-medium">
              {t("workspace.channels.tabs.config")}
            </div>
            <div className="bg-muted rounded-md p-3 max-h-60 overflow-y-auto">
              <ConfigSummary
                config={channel.config}
                selectedWidgetAgent={selectedWidgetAgent}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleCancel}>
            {t("workspace.agent.publish.dialog.buttons.cancel")}
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={publishStatus === FetchStatus.Loading || !isVersionValid}
          >
            {publishStatus === FetchStatus.Loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t("workspace.agent.publish.dialog.buttons.publish")}
              </>
            ) : (
              t("workspace.agent.publish.dialog.buttons.publish")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ChannelPublishDialog
